import type { ComponentProps } from 'react';
import { useState } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Upload } from '@/components/common/upload';

import { FormField } from '../form-field';

type UploadBaseProps = Omit<ComponentProps<typeof Upload>, 'value' | 'onChange'>;

export type UploadFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = UploadBaseProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  help?: string;
  required?: boolean;
};

export function UploadField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ name, control, label, help, required, ...rest }: UploadFieldProps<TFieldValues, TName>) {
  // Whether the dropzone currently holds anything at all — uploading, rejected or committed.
  // While it does, `<Upload>`'s own tile already explains itself; stacking the generic required
  // error underneath would be redundant, not additive (ADO 119326). Starts false: on mount there
  // is no error yet to suppress regardless of any seeded value.
  const [hasEntries, setHasEntries] = useState(false);
  return (
    <FormField
      name={name}
      control={control}
      label={label}
      help={help}
      required={required}
      hideError={hasEntries}
    >
      {({ field, fieldState }) => (
        <Upload
          {...rest}
          id={name}
          status={fieldState.error ? 'error' : undefined}
          // Upload seeds its list from value once at mount; use a key prop to remount on reset.
          value={field.value ?? []}
          onChange={field.onChange}
          onEntriesChange={setHasEntries}
        />
      )}
    </FormField>
  );
}
